import { Component } from '@theme/component';

/**
 * DAVIDsTEA homepage hero — carousel of full-width slides.
 *
 * The rail is a scroll-snap container, so a finger, a trackpad and the keyboard all
 * work before this file loads. The script only adds what scrolling cannot give on its
 * own: the dots, the two arrows, and the autoplay.
 *
 * Controls stay hidden until this runs, and hide again whenever a single slide leaves
 * the rail nothing to scroll — otherwise a one-slide hero would ship with an inert pair
 * of arrows and a single dot.
 *
 * The step is measured between the first two slides rather than assumed to be the rail
 * width. Slides are full-width today, but reading the real offset keeps the arrows
 * honest if the stylesheet ever insets them.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} rail
 * @property {HTMLButtonElement} [prev]
 * @property {HTMLButtonElement} [next]
 * @property {HTMLElement} [dots]
 *
 * @extends {Component<Refs>}
 */
export class DtHeroCarousel extends Component {
  requiredRefs = ['rail'];

  connectedCallback() {
    super.connectedCallback();

    const { rail, prev, next, dots } = this.refs;

    prev?.addEventListener('click', this.#onPrev);
    next?.addEventListener('click', this.#onNext);
    dots?.addEventListener('click', this.#onDotClick);
    rail.addEventListener('scroll', this.#onScroll, { passive: true });

    // Autoplay is a courtesy, not a hijack: attention stops it, and it resumes once attention
    // leaves. What counts as attention is the pointer anywhere over the carousel, or focus
    // anywhere inside it — unless the host opts out of the hover pause, see #hoverTargets.
    for (const target of this.#hoverTargets) {
      target.addEventListener('pointerenter', this.#pause);
      target.addEventListener('pointerleave', this.#resume);
    }
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#resume);
    document.addEventListener('visibilitychange', this.#onVisibility);

    this.#observer = new ResizeObserver(this.#sync);
    this.#observer.observe(rail);

    this.#buildDots();
    this.#sync();
    this.setAttribute('data-ready', '');
    this.#resume();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    const { rail, prev, next, dots } = this.refs;
    prev?.removeEventListener('click', this.#onPrev);
    next?.removeEventListener('click', this.#onNext);
    dots?.removeEventListener('click', this.#onDotClick);
    rail?.removeEventListener('scroll', this.#onScroll);
    for (const target of this.#hoverTargets) {
      target.removeEventListener('pointerenter', this.#pause);
      target.removeEventListener('pointerleave', this.#resume);
    }
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('focusout', this.#resume);
    document.removeEventListener('visibilitychange', this.#onVisibility);
    this.#observer?.disconnect();
    this.#pause();
  }

  /** @type {ResizeObserver | undefined} */
  #observer;

  /** @type {number | undefined} */
  #timer;

  /**
   * The hero's own slides unless the host names its own. dt-feature-banner does: its slides
   * are feature banners, and borrowing the hero's slide class would bring the hero's slide
   * styles with it on any page that carries both.
   */
  get #slides() {
    const selector = this.dataset.slideSelector || '.dt-hero-carousel__slide';
    return /** @type {HTMLElement[]} */ ([...this.refs.rail.querySelectorAll(selector)]);
  }

  /**
   * Dots for a host that cannot count its own slides. dt-feature-banner's second and third
   * slides are blocks that draw nothing when left empty, and a section cannot read a block's
   * settings to know which, so it leaves the dot row empty (`data-auto`) and it is filled here
   * from the slides that actually drew. `data-label` is the translated "slide x of y" with
   * [index] and [length] markers. One slide gets no dots, as on the hero.
   */
  #buildDots() {
    const { dots } = this.refs;
    if (!dots || !dots.hasAttribute('data-auto')) return;

    const count = this.#slides.length;
    const label = dots.dataset.label || '[index] / [length]';
    const dotClass = dots.dataset.dotClass || 'dt-hero-carousel__dot';

    dots.replaceChildren(
      ...Array.from({ length: count > 1 ? count : 0 }, (_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = dotClass;
        dot.dataset.index = String(index);
        dot.setAttribute('aria-label', label.replace('[index]', String(index + 1)).replace('[length]', String(count)));
        return dot;
      })
    );
  }

  /** One slide plus one gap, read from the DOM rather than assumed to be the rail width. */
  get #step() {
    const [first, second] = this.#slides;
    if (!first) return 0;
    if (second) return second.getBoundingClientRect().left - first.getBoundingClientRect().left;
    return first.getBoundingClientRect().width;
  }

  get #index() {
    const step = this.#step;
    if (!step) return 0;
    return Math.round(this.refs.rail.scrollLeft / step);
  }

  get #behavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /** @param {number} index */
  #goTo(index) {
    const step = this.#step;
    const count = this.#slides.length;
    if (!step || !count) return;
    // Wraps both ways so the arrows never dead-end and autoplay can loop.
    const target = ((index % count) + count) % count;
    // A wrap is a jump, not a journey: smooth-scrolling from the last slide back to the
    // first drags the whole rail past the reader's eyes, which reads as a glitch rather
    // than as a loop. Only steps between neighbours animate.
    const wrapped = Math.abs(target - this.#index) > 1;
    this.refs.rail.scrollTo({ left: step * target, behavior: wrapped ? 'auto' : this.#behavior });
  }

  #onPrev = () => {
    this.#goTo(this.#index - 1);
    this.#restart();
  };

  #onNext = () => {
    this.#goTo(this.#index + 1);
    this.#restart();
  };

  /** @param {Event} event */
  #onDotClick = (event) => {
    const dot = /** @type {HTMLElement} */ (event.target).closest('[data-index]');
    if (!(dot instanceof HTMLElement)) return;
    this.#goTo(Number(dot.dataset.index));
    this.#restart();
  };

  /**
   * `data-pause-on-hover="false"`: the pointer pauses autoplay only over the dots and arrows.
   *
   * dt-feature-banner sets it. Its slides are full-bleed campaign bands, 560px tall across the
   * whole window, so a pointer resting anywhere in the middle of the page is resting on one —
   * and a pause on hover there read as "autoplay is broken": the slider held its first slide for
   * as long as someone looked at it. Over the controls the pause still earns its place, so a
   * slide does not change under a dot about to be clicked.
   */
  get #pausesOnHover() {
    return this.dataset.pauseOnHover !== 'false';
  }

  get #hoverTargets() {
    if (this.#pausesOnHover) return [this];
    const { prev, next, dots } = this.refs;
    return /** @type {HTMLElement[]} */ ([prev, next, dots].filter(Boolean));
  }

  /**
   * Without the hover pause, focus only pauses when it came from the keyboard. A mouse click on
   * a dot also leaves focus on it, and with no pointerleave on the carousel to resume, autoplay
   * would stay stopped until the visitor happened to click somewhere else on the page. A
   * keyboard user moving through the slides still stops it, which is the pause they need.
   *
   * @param {FocusEvent} event
   */
  #onFocusIn = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (this.#pausesOnHover || target.matches(':focus-visible')) this.#pause();
  };

  #onScroll = () => this.#sync();

  #onVisibility = () => (document.hidden ? this.#pause() : this.#resume());

  #pause = () => {
    clearInterval(this.#timer);
    this.#timer = undefined;
  };

  #resume = () => {
    this.#pause();
    // A hero that cannot scroll has nothing to advance to, and a reader who asked for
    // reduced motion did not ask for a slideshow that moves on its own.
    if (this.dataset.autoplay !== 'true') return;
    if (this.#slides.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const delay = Number(this.dataset.delay || 5) * 1000;
    this.#timer = setInterval(() => this.#goTo(this.#index + 1), delay);
  };

  #restart = () => {
    // Advancing by hand should buy a full delay before the next automatic move,
    // not land the reader mid-interval on a slide they just chose.
    if (this.#timer) this.#resume();
  };

  /**
   * A single pixel of slack on each end: browsers land on fractional scroll offsets, and
   * an exact comparison marks the wrong dot at the last stop.
   */
  #sync = () => {
    const { rail, dots } = this.refs;
    const scrollable = rail.scrollWidth - rail.clientWidth > 1;
    this.toggleAttribute('data-scrollable', scrollable);

    const index = this.#index;
    this.#slides.forEach((slide, i) => {
      // Off-screen slides stay out of the tab order and out of the accessibility tree,
      // so a keyboard reader is not walked through three hidden buttons.
      slide.toggleAttribute('inert', scrollable && i !== index);
      slide.setAttribute('aria-hidden', String(scrollable && i !== index));
    });

    if (!dots) return;
    [...dots.querySelectorAll('[data-index]')].forEach((dot, i) => {
      dot.toggleAttribute('data-current', i === index);
      dot.setAttribute('aria-current', String(i === index));
    });
  };
}

if (!customElements.get('dt-hero-carousel-component')) {
  customElements.define('dt-hero-carousel-component', DtHeroCarousel);
}
